/**
 * Which statements the Financials tab opens on. Group (consolidated) accounts win,
 * unless the company has none, or its filed single-entity (individual) accounts are
 * more than one fiscal year newer than the newest group figure, i.e. the group
 * stopped reporting and would show outdated figures.
 *
 * Mirrors the backend's `chooseStatementScope` (td-backend
 * lambdas-auxbe/resources/utils.js), which picks the scope of the Overview tab and
 * sends it as `financial_overview_scope`. The dataMapper uses that value when the
 * response carries it; this is the fallback for responses without it.
 *
 * The newest group figure is the group's filed accounts or its self-disclosed
 * revenue: a recent self-disclosed group turnover shows the group is still around.
 * Other self-disclosed rows (`source === 'basic'`, e.g. a headcount) say nothing
 * about group reporting and are skipped, and the single-entity side counts filed
 * accounts only. Self-disclosed rows are dated 31 December of the year they were
 * collected, so they would otherwise make a single-entity scope look current.
 *
 * "Newer" compares the calendar year each fiscal year ends in (`_year`), not the
 * exact period: months and short fiscal years (Rumpfbilanz) are not weighed.
 */

const SELF_DISCLOSED_REVENUE_CODE = 'sd.revenue';

const isFiled = (row) => row.source !== 'basic';

const isSelfDisclosedRevenue = (row) => row.xbrl_code === SELF_DISCLOSED_REVENUE_CODE;

const hasUsableYear = (row) => Number.isFinite(row._year) && row._year > 0;

/**
 * Newest fiscal year among the rows that count, or null when there is none.
 * Rows without a usable year (missing, NaN, or 0 from an empty date) are skipped.
 *
 * @param {object[]} rows Statement rows carrying `_year`, `source` and `xbrl_code`.
 * @param {(row: object) => boolean} counts Whether a row's year counts.
 * @returns {number|null}
 */
const latestYear = (rows, counts) =>
  rows.reduce((latest, row) => {
    if (!counts(row) || !hasUsableYear(row)) return latest;
    return latest === null || row._year > latest ? row._year : latest;
  }, null);

/**
 * @param {object[]} consolidatedRows Consolidated statement rows as the
 *   `/get-profiler-details` dataMapper leaves them (with `_year` set).
 * @param {object[]} individualRows Individual statement rows, same shape.
 * @returns {boolean} true when the tab should open on Individual.
 */
export const shouldOpenIndividualFinancials = (consolidatedRows = [], individualRows = []) => {
  if (!consolidatedRows.length) {
    return true;
  }

  const latestIndividual = latestYear(individualRows, isFiled);
  if (latestIndividual === null) {
    return false;
  }

  const latestGroup = latestYear(
    consolidatedRows,
    (row) => isFiled(row) || isSelfDisclosedRevenue(row)
  );
  return latestGroup === null || latestIndividual > latestGroup + 1;
};
